import React from 'react';

import styled from 'styled-components';

import { VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';

import RatingCard from './RatingCard';
import { ReviewsSummaryRatings } from './types';
import { shouldDisplayValueDifference } from '../valueDifferenceUtils';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 20px;
`;

type Props = {
  ratings: ReviewsSummaryRatings | null;
  comparisonTypeEnabled?: boolean;
  valueDisplay?: number;
};

const computeDifference = (
  averageRating: number | null,
  difference: number | null,
  showDifference: boolean,
  valueDisplay?: number
) => {
  if (!showDifference || averageRating === null || difference === null) {
    return undefined;
  }

  if (valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
    if (averageRating === 0) return null;
    return Math.round((difference / averageRating) * 100);
  }

  return difference;
};

export const RatingsSection = ({ ratings, comparisonTypeEnabled, valueDisplay }: Props) => {
  if (!ratings) {
    return null;
  }

  const showDifference = shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay);

  const { overall, by_source } = ratings;

  const allRatings = [
    {
      name: 'OVERALL',
      rating: overall.average_rating,
      difference: computeDifference(overall.average_rating, overall.difference, showDifference, valueDisplay),
    },
    ...by_source.map((source) => ({
      name: source.source.toUpperCase(),
      rating: source.average_rating,
      difference: computeDifference(source.average_rating, source.difference, showDifference, valueDisplay),
    })),
  ];

  return (
    <Container>
      {allRatings.map((rating, index) => (
        <RatingCard
          key={index}
          name={rating.name}
          rating={rating.rating}
          difference={rating.difference}
          valueDisplay={valueDisplay}
        />
      ))}
    </Container>
  );
};

export default RatingsSection;
